import { Injectable } from '@angular/core';
import { TranslationKeys } from '@internationalization/translation-keys';
import {
  OnboardingProcessStage,
  OnboardingProcessStageSubmittedDocuments,
} from '@interfaces/a-organizar/onboardingCompanyInformation';
import { TranslateService } from '@ngx-translate/core';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { OnboardingDocumentsTranslationService } from '@src/app/features/onboarding/v1/components/helpers/translate-helper';

export interface DocumentAnalysisPresentationMaps {
  documentLabels: Record<string, Record<string, string>>;
  submissionTooltips: Record<string, string>;
  analysisTooltips: Record<string, string>;
}

/**
 * Monta os textos derivados (rótulos de documento e tooltips de
 * submissão/análise) usados por `DocumentAnalysisCompanyComponent`. Extraído
 * do componente pai na Task 10 (limpeza) para reduzir seu tamanho — pura
 * montagem de string a partir de `stages`, sem HTTP, dialogs ou estado de
 * expansão, que continuam no componente.
 */
@Injectable({
  providedIn: 'root',
})
export class DocumentAnalysisPresentationService {
  private tk = TranslationKeys;
  private onboardingDocumentsTranslationService: OnboardingDocumentsTranslationService;

  constructor(
    private translateService: TranslateService,
    private dateFormatter: DateFormatterService
  ) {
    this.onboardingDocumentsTranslationService = new OnboardingDocumentsTranslationService(
      translateService
    );
  }

  buildPresentationMaps(stages: OnboardingProcessStage[]): DocumentAnalysisPresentationMaps {
    const documentLabels: Record<string, Record<string, string>> = {};
    const submissionTooltips: Record<string, string> = {};
    const analysisTooltips: Record<string, string> = {};

    for (const stage of stages) {
      documentLabels[stage.name] = (stage.requiredDocuments ?? []).reduce<Record<string, string>>(
        (map, required) => {
          map[required.name] = this.onboardingDocumentsTranslationService.getTranslationForDocument(
            stage.name,
            required.name
          );

          return map;
        },
        {}
      );

      for (const required of stage.requiredDocuments ?? []) {
        for (const document of required.submittedDocuments ?? []) {
          if (!document.id) {
            continue;
          }

          submissionTooltips[document.id] = this.getSubmissionTooltip(document);
          analysisTooltips[document.id] = this.getAnalysisTooltip(document);
        }
      }
    }

    return { documentLabels, submissionTooltips, analysisTooltips };
  }

  private t(key: string): string {
    return this.translateService.instant(key) || key;
  }

  /**
   * Generates the tooltip message for document submission information
   */
  private getSubmissionTooltip(document: OnboardingProcessStageSubmittedDocuments): string {
    if (!document.createdByUser) {
      return '';
    }

    return this.t(this.tk.Onboarding.SentByUserOnDate)
      .replace('{user}', document.createdByUser?.firstName)
      .replace('{date}', this.dateFormatter.formatDateTimeInstant(document.createdAt));
  }

  /**
   * Generates the tooltip message for document analysis information
   */
  private getAnalysisTooltip(document: OnboardingProcessStageSubmittedDocuments): string {
    if (!document.analysisByUser) {
      return '';
    }

    return this.t(this.tk.Onboarding.AnalyzedByUser)
      .replace('{user}', document.analysisByUser?.firstName)
      .replace('{date}', this.dateFormatter.formatDateTimeInstant(document.analysisDate));
  }
}
